import React, { useState, useEffect } from 'react';
import ApiClient from '../../utils/ApiClient';

function PricingBreakdownReport() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [availableJobs, setAvailableJobs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(50);
    const [expandedRows, setExpandedRows] = useState(new Set());
    
    const [filters, setFilters] = useState({
        jobId: '',
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        pricingMethod: '',
        searchTerm: '',
        sortField: 'created_at',
        sortDirection: 'DESC'
    });

    useEffect(() => {
        fetchAvailableJobs();
    }, []);

    useEffect(() => {
        fetchData();
        fetchSummary();
    }, [filters, currentPage]);

    const fetchAvailableJobs = async () => {
        try {
            const response = await ApiClient.get('/api/pricing-breakdown/jobs');
            setAvailableJobs(response.data || []);
        } catch (err) {
            console.error('Error fetching available jobs:', err);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const requestData = {
                ...filters,
                page: currentPage,
                size: pageSize
            };
            
            const response = await ApiClient.post('/api/pricing-breakdown/data', requestData);
            const result = response.data;
            
            setData(result.data || []);
            setTotalCount(result.totalCount || 0);
        } catch (err) {
            setError('Failed to load pricing breakdown data');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.jobId) params.append('jobId', filters.jobId);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            
            const response = await ApiClient.get(`/api/pricing-breakdown/summary?${params}`);
            setSummary(response.data);
        } catch (err) {
            console.error('Error fetching summary:', err);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setCurrentPage(0); // Reset to first page when filters change
    };

    const handleSort = (field) => {
        const newDirection = filters.sortField === field && filters.sortDirection === 'ASC' ? 'DESC' : 'ASC';
        setFilters(prev => ({
            ...prev,
            sortField: field,
            sortDirection: newDirection
        }));
        setCurrentPage(0);
    };

    const handleExport = async () => {
        try {
            setLoading(true);
            const requestData = {
                ...filters,
                // No pagination for export
                page: null,
                size: null
            };
            
            const response = await ApiClient.post('/api/pricing-breakdown/export', requestData);
            const { csvData, filename } = response.data;
            
            // Create and download CSV file
            const blob = new Blob([csvData], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting data:', err);
            alert('Error exporting data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleRowExpansion = (rowId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    };

    const formatPrice = (price) => {
        if (price == null) return 'N/A';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const getConditionName = (conditionId) => {
        const conditions = {
            11: 'New',
            1: 'Like New',
            2: 'Very Good',
            3: 'Good',
            4: 'Acceptable'
        };
        return conditions[conditionId] || `Condition ${conditionId}`;
    };

    const getPricingMethodColor = (method) => {
        if (method === 'lowest_price_per_condition') return 'bg-green-100 text-green-800';
        if (method.includes('fallback')) return 'bg-yellow-100 text-yellow-800';
        if (method.includes('error')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-red-600 mb-4">{error}</div>
                <button 
                    onClick={fetchData}
                    className="text-blue-600 hover:text-blue-800"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-2xl font-bold text-gray-900">{summary.total_items}</div>
                        <div className="text-sm text-gray-600">Total Items</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-2xl font-bold text-green-600">{summary.competitive_pricing_count}</div>
                        <div className="text-sm text-gray-600">Competitive Pricing</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-2xl font-bold text-yellow-600">{summary.map_fallback_count}</div>
                        <div className="text-sm text-gray-600">MAP Fallbacks</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-2xl font-bold text-red-600">{summary.error_count}</div>
                        <div className="text-sm text-gray-600">Errors</div>
                    </div>
                </div>
            )}

            {/* Filters Section */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {/* Job ID Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sync Job
                        </label>
                        <select
                            value={filters.jobId}
                            onChange={(e) => handleFilterChange('jobId', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                            <option value="">All Jobs</option>
                            {availableJobs.map(job => (
                                <option key={job.job_id} value={job.job_id}>
                                    {job.job_id.substring(0, 8)}... ({job.item_count} items)
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Start Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                    </div>

                    {/* End Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                    </div>

                    {/* Pricing Method Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pricing Method
                        </label>
                        <select
                            value={filters.pricingMethod}
                            onChange={(e) => handleFilterChange('pricingMethod', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                            <option value="">All Methods</option>
                            <option value="lowest_price_per_condition">Competitive Pricing</option>
                            <option value="percentage_off_map_fallback">MAP Fallback</option>
                            <option value="error_fallback_to_map">Error Fallback</option>
                        </select>
                    </div>

                    {/* Search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Search SKU/ASIN
                        </label>
                        <input
                            type="text"
                            placeholder="Enter SKU or ASIN"
                            value={filters.searchTerm}
                            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleExport}
                        disabled={loading || data.length === 0}
                        className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                        Pricing Breakdown Data ({totalCount} items)
                    </h3>
                </div>

                {loading && (
                    <div className="text-center py-12">
                        <div className="text-gray-600">Loading...</div>
                    </div>
                )}

                {!loading && data.length === 0 && (
                    <div className="text-center py-12 text-gray-600">
                        No data found for the selected filters.
                    </div>
                )}

                {!loading && data.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Details
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('merchant_sku')}
                                    >
                                        SKU
                                        {filters.sortField === 'merchant_sku' && (
                                            <span className="ml-1">{filters.sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('asin')}
                                    >
                                        ASIN
                                        {filters.sortField === 'asin' && (
                                            <span className="ml-1">{filters.sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Condition
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('map_price')}
                                    >
                                        MAP Price
                                        {filters.sortField === 'map_price' && (
                                            <span className="ml-1">{filters.sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('competitive_price')}
                                    >
                                        Competitive Price
                                        {filters.sortField === 'competitive_price' && (
                                            <span className="ml-1">{filters.sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('submitted_price')}
                                    >
                                        Submitted Price
                                        {filters.sortField === 'submitted_price' && (
                                            <span className="ml-1">{filters.sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Method
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('created_at')}
                                    >
                                        Date
                                        {filters.sortField === 'created_at' && (
                                            <span className="ml-1">{filters.sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() => toggleRowExpansion(row.id)}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    {expandedRows.has(row.id) ? '−' : '+'}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {row.merchant_sku}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {row.asin}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {getConditionName(row.condition_id)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatPrice(row.map_price)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatPrice(row.competitive_price)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {formatPrice(row.submitted_price)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPricingMethodColor(row.pricing_method)}`}>
                                                    {row.pricing_method.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {new Date(row.created_at).toLocaleDateString()}
                                            </td>
                                        </tr>
                                        {expandedRows.has(row.id) && (
                                            <tr>
                                                <td colSpan="9" className="px-6 py-4 bg-gray-50">
                                                    <div className="space-y-4">
                                                        <div>
                                                            <h4 className="font-medium text-gray-900 mb-2">Pricing Details</h4>
                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                                <div>
                                                                    <span className="font-medium">Threshold Price:</span> {formatPrice(row.threshold_price)}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Tiered Price:</span> {formatPrice(row.tiered_price)}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Price ID:</span> {row.price_id || 'N/A'}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Threshold Enforced:</span> {row.threshold_enforced ? 'Yes' : 'No'}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Pricing Decision:</span> {row.pricing_decision || 'N/A'}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Job ID:</span> {row.job_id}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Created:</span> {new Date(row.created_at).toLocaleString()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {row.calculation_details && (
                                                            <div>
                                                                <h4 className="font-medium text-gray-900 mb-2">Calculation Details</h4>
                                                                <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
                                                                    {JSON.stringify(JSON.parse(row.calculation_details), null, 2)}
                                                                </pre>
                                                            </div>
                                                        )}
                                                        
                                                        {row.competitive_data && (
                                                            <div>
                                                                <h4 className="font-medium text-gray-900 mb-2">Competitive Data</h4>
                                                                <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
                                                                    {JSON.stringify(JSON.parse(row.competitive_data), null, 2)}
                                                                </pre>
                                                            </div>
                                                        )}
                                                        
                                                        {row.decision_details && (
                                                            <div>
                                                                <h4 className="font-medium text-gray-900 mb-2">Decision Details</h4>
                                                                <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
                                                                    {JSON.stringify(JSON.parse(row.decision_details), null, 2)}
                                                                </pre>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                                disabled={currentPage === 0}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                                disabled={currentPage >= totalPages - 1}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                                    <span className="font-medium">
                                        {Math.min((currentPage + 1) * pageSize, totalCount)}
                                    </span>{' '}
                                    of <span className="font-medium">{totalCount}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                                        disabled={currentPage === 0}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                                        disabled={currentPage >= totalPages - 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PricingBreakdownReport;